import { Box, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Tooltip } from "@mui/material"
import {
  AddOutlined,
  DeleteOutlined,
  EditOutlined,
  HelpOutlined,
  MoreVertOutlined,
  UndoOutlined,
} from "@mui-symbols-material/w300"
import type { ICellRendererParams, IHeaderParams, IRowNode } from "ag-grid-community"
import { type FC, useEffect, useReducer, useRef, useState } from "react"

import { MAX_TIERS_PER_GROUP } from "./constants"
import type { PricingGridRow, RowActionsParams } from "./types"
import { getRowActionsState } from "./utils"

import { AerosCheckbox } from "@/components/UiKit"
import { CURRENCY_RENDER_PRECISION } from "@/constants/formats"
import { formatNumber } from "@/utils/number"
import { isNil } from "@/utils/utils"

const BillBack: FC<ICellRendererParams<PricingGridRow, number>> = ({ value, data }) => {
  const previousBillBack = data?.item?.previousBillBack
  const hasBillBack = !isNil(value)
  const hasPreviousBillBack = !isNil(previousBillBack)

  if (!hasBillBack) return null

  return (
    <Box className='flex h-full items-center justify-end gap-4'>
      <Box>
        {value !== null &&
          value !== undefined &&
          `$${formatNumber(value, CURRENCY_RENDER_PRECISION, CURRENCY_RENDER_PRECISION)}`}
      </Box>
      <Tooltip
        title={
          hasPreviousBillBack
            ? `Previous BB: $${formatNumber(previousBillBack, CURRENCY_RENDER_PRECISION, CURRENCY_RENDER_PRECISION)}`
            : "No previous BB"
        }
        placement='top'
      >
        <HelpOutlined className='cursor-pointer text-sm' />
      </Tooltip>
    </Box>
  )
}

const SelectionHeader: FC<IHeaderParams<PricingGridRow>> = ({ api }) => {
  const [, forceUpdate] = useReducer((x: number) => x + 1, 0)

  useEffect(() => {
    api.addEventListener("selectionChanged", forceUpdate)
    return () => api.removeEventListener("selectionChanged", forceUpdate)
  }, [api])

  const allNodes: IRowNode<PricingGridRow>[] = []
  api.forEachNode((node) => allNodes.push(node))

  const selectedCount = allNodes.filter((node) => node.isSelected()).length
  const allSelected = allNodes.length > 0 && selectedCount === allNodes.length
  const someSelected = selectedCount > 0 && !allSelected

  return (
    <AerosCheckbox
      checked={allSelected}
      indeterminate={someSelected}
      size='small'
      onChange={(checked) => (checked ? api.selectAll() : api.deselectAll())}
      sx={{ padding: 0 }}
    />
  )
}

const Selection: FC<ICellRendererParams<PricingGridRow>> = ({ node, api }) => {
  const [, forceUpdate] = useReducer((x: number) => x + 1, 0)

  useEffect(() => {
    api.addEventListener("selectionChanged", forceUpdate)
    return () => api.removeEventListener("selectionChanged", forceUpdate)
  }, [api])

  const rowIndex = node.rowIndex
  const itemId = node.data?.item.priceChangeItemId

  // Not the first row in this item group — render nothing
  if (
    rowIndex === null ||
    (rowIndex > 0 && api.getDisplayedRowAtIndex(rowIndex - 1)?.data?.item.priceChangeItemId === itemId)
  ) {
    return <span />
  }

  // Collect all nodes belonging to this item group (by priceChangeItemId)
  const groupNodes: IRowNode<PricingGridRow>[] = []
  for (let i = rowIndex; i < api.getDisplayedRowCount(); i++) {
    const n = api.getDisplayedRowAtIndex(i)
    if (!n?.data || n.data.item.priceChangeItemId !== itemId) break
    groupNodes.push(n)
  }

  const selectedCount = groupNodes.filter((n) => n.isSelected()).length
  const allSelected = groupNodes.length > 0 && selectedCount === groupNodes.length
  const someSelected = selectedCount > 0 && !allSelected

  return (
    <div className='pt-0'>
      <AerosCheckbox
        checked={allSelected}
        indeterminate={someSelected}
        size='small'
        onChange={(checked) => api.setNodesSelected({ nodes: groupNodes, newValue: checked })}
        sx={{ padding: 0 }}
      />
    </div>
  )
}

const RowActions: FC<RowActionsParams> = ({
  node,
  api,
  onAddPriceTier,
  onClearPriceTier,
  onEditSurcharge,
  isRowNew,
  variant,
  readonly,
}) => {
  const anchorElRef = useRef<HTMLButtonElement>(null)
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [, forceUpdate] = useReducer((x: number) => x + 1, 0)

  useEffect(() => {
    api.addEventListener("rowDataUpdated", forceUpdate)
    return () => api.removeEventListener("rowDataUpdated", forceUpdate)
  }, [api])

  const { isAddDisabled, clearPriceTierLabel, isUndoAction } = getRowActionsState(node, api, isRowNew, variant)

  const handleMenuOpen = () => setIsMenuOpen(true)
  const handleMenuClose = () => setIsMenuOpen(false)

  const withClose = (fn: () => void) => () => {
    handleMenuClose()
    fn()
  }

  return (
    <Box ref={anchorElRef} className='flex items-center justify-center'>
      <IconButton size='small' onClick={handleMenuOpen}>
        <MoreVertOutlined />
      </IconButton>
      <Menu anchorEl={anchorElRef.current} open={isMenuOpen} onClose={handleMenuClose}>
        <MenuItem disabled={readonly} onClick={withClose(() => onEditSurcharge(node.rowIndex ?? -1))}>
          <ListItemIcon>
            <EditOutlined sx={{ fontVariationSettings: '"wght" 300' }} />
          </ListItemIcon>
          <ListItemText primary='Edit Surcharge' />
        </MenuItem>
        <Tooltip
          title={isAddDisabled ? `Maximum ${MAX_TIERS_PER_GROUP} tiers per pricing package` : ""}
          placement='left'
        >
          <span>
            <MenuItem
              disabled={isAddDisabled || readonly}
              onClick={withClose(() => onAddPriceTier(node.rowIndex ?? -1))}
            >
              <ListItemIcon>
                <AddOutlined sx={{ fontVariationSettings: '"wght" 300' }} />
              </ListItemIcon>
              <ListItemText primary='Add Price Tier' />
            </MenuItem>
          </span>
        </Tooltip>
        <MenuItem disabled={readonly} onClick={withClose(() => onClearPriceTier(node.rowIndex ?? -1))}>
          <ListItemIcon>
            {isUndoAction ? (
              <UndoOutlined sx={{ fontVariationSettings: '"wght" 300' }} />
            ) : (
              <DeleteOutlined sx={{ fontVariationSettings: '"wght" 300' }} />
            )}
          </ListItemIcon>
          <ListItemText primary={clearPriceTierLabel} />
        </MenuItem>
      </Menu>
    </Box>
  )
}

const RequiredColumnHeader: FC<IHeaderParams<PricingGridRow>> = ({ displayName }) => (
  <span className='flex items-center'>
    {displayName}
    <span className='ml-0.5 text-red-500'>*</span>
  </span>
)

export const GridCells = {
  BillBack,
  RowActions,
  RequiredColumnHeader,
  Selection,
  SelectionHeader,
}
